import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactLoading from "react-loading"
import { useToaster } from "@hellocontento/maillard"
import {
  CenterStack,
  FloatingSpinner,
  FloatingMessage,
  FailureMessage,
  StatusCard,
  StepBody,
  StepConnector,
  StepConnectorMuted,
  StepConnectorError,
  StepIconActive,
  StepIconDone,
  StepIconInactive,
  StepIconError,
  StepLeft,
  StepRow,
  StepSubtitle,
  StepTitle,
  UpdatePill,
  UpdateText,
  ErrorText,
  Muted,
  InlineTitle,
  InlineSubtitle,
} from "../styles"
import { STATUS_FLOW, getSteps } from "../values"
import { Health, PostBatchUpdate, Status } from "../types"
import { formatCountdown } from "../utils"
import Button from "components/common/Button"

const DISPLAY_INTERVAL_MS = 500

const getIndexOfStatus = (status: Status) => STATUS_FLOW.indexOf(status)

const stepIndexForStatus = (status: Status | null | undefined, steps: ReturnType<typeof getSteps>, fallback = 0): number => {
  if (!status) return fallback
  const statusIndex = getIndexOfStatus(status)

  for (let stepIndex = steps.length - 1; stepIndex >= 0; stepIndex--) {
    if (statusIndex >= getIndexOfStatus(steps[stepIndex].started)) return stepIndex
  }
  return fallback
}

type GenerationLoaderProps = {
  status?: Status
  health?: Health
  updates?: PostBatchUpdate[]
  estimatedCompletionAt?: number | null
  sourceType?: string
  // true → [read source, create profile]; false → [read source only].
  showProfileStep: boolean
  onDismiss: () => void
}

const GenerationLoader: React.FC<GenerationLoaderProps> = ({
  status,
  health,
  updates,
  estimatedCompletionAt,
  sourceType,
  showProfileStep,
  onDismiss,
}) => {
  const addToast = useToaster()

  // ─── State ─────────────────────────────────────────────────────────────────
  const [displayedMessage, setDisplayedMessage] = useState<string | null>(null)
  const [displayedStepIndex, setDisplayedStepIndex] = useState<number>(-1)

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const lastEnqueuedUpdateDatetimeMsRef = useRef<number>(0)
  const failedToastShownRef = useRef(false)
  const activeStepIndexRef = useRef(0)
  const hasInitializedRef = useRef(false)
  const stepMessageQueuesRef = useRef<PostBatchUpdate[][]>([])
  const playbackIntervalIdRef = useRef<number | null>(null)
  const lastCommittedDisplayRef = useRef<{ stepIndex: number; message: string | null }>({ stepIndex: -1, message: null })

  // ─── Steps (depend on whether a profile is being created) ───────────────────
  const STEPS = useMemo(() => {
    const all = getSteps(sourceType)
    return showProfileStep ? all : all.slice(0, 1)
  }, [sourceType, showProfileStep])

  const doneStatus = showProfileStep ? ("create_profile_done" as Status) : ("create_source_done" as Status)

  // ─── Derived Values ────────────────────────────────────────────────────────
  const isFailed = health === "failed"
  const isDone = !!status && getIndexOfStatus(status) >= getIndexOfStatus(doneStatus)
  const countdown = formatCountdown(estimatedCompletionAt)
  const activeStepIndex = useMemo(() => stepIndexForStatus(status, STEPS), [status, STEPS])

  // ─── Callbacks ─────────────────────────────────────────────────────────────
  const commitDisplayedMessage = useCallback((stepIndex: number, message: string | null) => {
    const previous = lastCommittedDisplayRef.current
    if (previous.stepIndex === stepIndex && previous.message === message) return

    lastCommittedDisplayRef.current = { stepIndex, message }
    setDisplayedStepIndex(stepIndex)
    setDisplayedMessage(message)
  }, [])

  const stopPlayback = useCallback(() => {
    if (playbackIntervalIdRef.current) window.clearInterval(playbackIntervalIdRef.current)
    playbackIntervalIdRef.current = null
  }, [])

  const startPlayback = useCallback(() => {
    stopPlayback()

    playbackIntervalIdRef.current = window.setInterval(() => {
      const stepIndex = activeStepIndexRef.current
      const queue = stepMessageQueuesRef.current[stepIndex]
      if (!queue || !queue.length) return

      const nextMessage = queue.shift()!
      commitDisplayedMessage(stepIndex, nextMessage.message)
    }, DISPLAY_INTERVAL_MS)
  }, [stopPlayback, commitDisplayedMessage])

  // ─── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    activeStepIndexRef.current = activeStepIndex
  }, [activeStepIndex])

  useEffect(() => {
    failedToastShownRef.current = false
    hasInitializedRef.current = false
    activeStepIndexRef.current = 0
    lastEnqueuedUpdateDatetimeMsRef.current = 0
    stepMessageQueuesRef.current = Array.from({ length: showProfileStep ? 2 : 1 }, () => [])
    lastCommittedDisplayRef.current = { stepIndex: -1, message: null }

    startPlayback()

    return () => stopPlayback()
  }, [showProfileStep, startPlayback, stopPlayback])

  // Fold the `updates` prop into the per-step playback queues. On the first run
  // we seed the watermark to the newest update already present, so a (re)mount
  // mid-generation shows the latest message immediately rather than replaying
  // the whole history.
  useEffect(() => {
    const list = updates ?? []
    const nextActiveStepIndex = stepIndexForStatus(status, STEPS)

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      activeStepIndexRef.current = nextActiveStepIndex

      const latest = list.at(-1)
      if (latest?.datetimeMs) {
        lastEnqueuedUpdateDatetimeMsRef.current = latest.datetimeMs
        commitDisplayedMessage(stepIndexForStatus(latest.status, STEPS, nextActiveStepIndex), latest.message)
      }
      return
    }

    const newUpdates = list.filter(update => (update?.datetimeMs ?? 0) > lastEnqueuedUpdateDatetimeMsRef.current)

    if (nextActiveStepIndex > activeStepIndexRef.current) {
      for (let i = 0; i < nextActiveStepIndex; i++) stepMessageQueuesRef.current[i] = []
    }

    for (const update of newUpdates) {
      if (!update?.message || !update?.datetimeMs) continue

      const stepIndex = stepIndexForStatus(update.status, STEPS, activeStepIndexRef.current)
      if (stepIndex >= nextActiveStepIndex && stepMessageQueuesRef.current[stepIndex]) {
        stepMessageQueuesRef.current[stepIndex].push(update)
      }
    }

    if (newUpdates.length > 0) lastEnqueuedUpdateDatetimeMsRef.current = newUpdates.at(-1)!.datetimeMs
    activeStepIndexRef.current = nextActiveStepIndex
  }, [updates, status, STEPS, commitDisplayedMessage])

  useEffect(() => {
    if (!isFailed || failedToastShownRef.current) return

    const lastUpdate = (updates ?? []).at(-1)
    const failureStepIndex = stepIndexForStatus(lastUpdate?.status, STEPS, activeStepIndexRef.current)
    const failureStatus = lastUpdate?.status || STEPS[failureStepIndex]?.started || "pending"
    const failureMessage = lastUpdate?.message || "Failed while generating."
    const failureDatetimeMs = lastUpdate?.datetimeMs || 0

    stepMessageQueuesRef.current[failureStepIndex]?.push({ status: failureStatus as Status, datetimeMs: failureDatetimeMs, message: failureMessage })
    addToast(failureMessage, "error")
    failedToastShownRef.current = true
  }, [isFailed, updates, STEPS, addToast])

  return (
    <CenterStack>
      {!isFailed && (
        <FloatingSpinner>
          <ReactLoading type={"spin"} color={"#bbb"} width={48} />
        </FloatingSpinner>
      )}

      <StatusCard>
        {STEPS.map((step, stepIndex) => {
          const isStepDone = isDone || stepIndex < activeStepIndex
          const isStepActive = stepIndex === activeStepIndex && !isDone && !isFailed
          const isStepFailed = isFailed && stepIndex === activeStepIndex
          const shouldShowUpdateHere = !isDone && displayedStepIndex === stepIndex

          const icon = isStepFailed ? (
            <StepIconError aria-hidden>!</StepIconError>
          ) : isStepDone ? (
            <StepIconDone aria-hidden>✓</StepIconDone>
          ) : isStepActive ? (
            <StepIconActive aria-hidden>{stepIndex + 1}</StepIconActive>
          ) : (
            <StepIconInactive aria-hidden>{stepIndex + 1}</StepIconInactive>
          )

          const connector =
            stepIndex === STEPS.length - 1 ? null : isStepFailed ? (
              <StepConnectorError aria-hidden />
            ) : isStepDone ? (
              <StepConnector aria-hidden />
            ) : (
              <StepConnectorMuted aria-hidden />
            )

          const body =
            isStepDone || isStepActive ? (
              <>
                <StepTitle>{step.title}</StepTitle>
                <StepSubtitle>{step.subtitle}</StepSubtitle>
              </>
            ) : (
              <Muted>
                <StepTitle>{step.title}</StepTitle>
                <StepSubtitle>{step.subtitle}</StepSubtitle>
              </Muted>
            )

          return (
            <StepRow key={step.key}>
              <StepLeft>
                {icon}
                {connector}
              </StepLeft>

              <StepBody>
                {body}

                {shouldShowUpdateHere && !isFailed && (
                  <UpdatePill>
                    <UpdateText>{displayedMessage}</UpdateText>
                  </UpdatePill>
                )}

                {shouldShowUpdateHere && isFailed && <ErrorText>{displayedMessage}</ErrorText>}
              </StepBody>
            </StepRow>
          )
        })}
      </StatusCard>

      {countdown && !isFailed && !isDone && (
        <FloatingMessage
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <InlineSubtitle style={{ fontSize: 12, opacity: 0.6 }}>Approx. {countdown} remaining</InlineSubtitle>
        </FloatingMessage>
      )}

      {isFailed && (
        <FailureMessage
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <InlineTitle>Generation failed</InlineTitle>
          <InlineSubtitle>Please try again.</InlineSubtitle>
          <Button onClick={onDismiss} variant="primary" size="lg" style={{ marginTop: 12 }}>
            Dismiss
          </Button>
        </FailureMessage>
      )}
    </CenterStack>
  )
}

export default GenerationLoader
